import { useState, useEffect } from 'react';
import { Users, TrendingUp, Target, ChevronRight, Award, BarChart3 } from 'lucide-react';
import apiClient from '../../api/client';

/**
 * Team Dashboard component showing aggregated team progress
 */
const TeamDashboard = ({ teamId, onClose }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (teamId) {
      loadDashboard();
    }
  }, [teamId]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/v1/goals/team/${teamId}/dashboard`);
      setDashboard(response.data.data);
    } catch (error) {
      console.error('Error loading team dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const value = cents / 100;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 75) return 'bg-blue-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-pulse">Loading team dashboard...</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        Failed to load team dashboard
      </div>
    );
  }

  const { team, summary, members, team_goals } = dashboard;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-6 h-6" />
              <h2 className="text-xl font-bold">{team.name}</h2>
            </div>
            {team.description && (
              <p className="text-indigo-100 text-sm">{team.description}</p>
            )}
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/80 hover:text-white">
              ✕
            </button>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{summary.member_count}</div>
            <div className="text-xs text-indigo-200">Members</div>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{formatCurrency(summary.total_achieved_revenue_cents)}</div>
            <div className="text-xs text-indigo-200">Achieved</div>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{formatCurrency(summary.total_target_revenue_cents)}</div>
            <div className="text-xs text-indigo-200">Target</div>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{summary.revenue_progress_percent}%</div>
            <div className="text-xs text-indigo-200">Progress</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-white/20 rounded-full h-3">
            <div
              className="bg-white h-3 rounded-full transition-all"
              style={{ width: `${Math.min(summary.revenue_progress_percent, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Member Rankings */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-500" />
          Team Rankings
        </h3>

        {members.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            No member data available
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((member) => (
              <div
                key={member.user_id}
                className={`p-4 rounded-lg border ${
                  member.rank <= 3 ? 'border-yellow-200 bg-yellow-50/50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold w-8">{getRankBadge(member.rank)}</span>
                    <div>
                      <div className="font-medium text-gray-900">{member.name}</div>
                      <div className="text-xs text-gray-500">
                        {member.role} • {member.goals_count} goal{member.goals_count !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${
                      member.progress_percent >= 100 ? 'text-green-600' : 'text-gray-900'
                    }`}>
                      {member.progress_percent}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatCurrency(member.achieved_revenue_cents)} / {formatCurrency(member.target_revenue_cents)}
                    </div>
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${getProgressColor(member.progress_percent)}`}
                    style={{ width: `${Math.min(member.progress_percent, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Team Goals */}
      {team_goals && team_goals.length > 0 && (
        <div className="px-6 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-500" />
            Team Goals
          </h3>
          <div className="grid gap-3">
            {team_goals.map((goal) => (
              <div key={goal.id} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="font-medium text-gray-900">{goal.name}</div>
                <div className="text-sm text-gray-600">
                  Target: {formatCurrency(goal.target_revenue_cents)} • {goal.period}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDashboard;
